import React from 'react';
import TransactionListViewerComponent from '../components/transaction/TransactionListViewerComponent';
import { RootState } from '../redux/store';
import { connect, ConnectedProps } from 'react-redux';
import { FormicaProps, withFormica } from '../helpers/withFormica';
import ComponentName from '../constant/ComponentName';
import { TransactionJsonModel, TransactionSchema } from '../domain/stream/transaction/TransactionDomains';
import CollectionNames from '../constant/CollectionNames';
import { addPagePath, changePageFilter, changePagePath, clearPage } from '../redux/actions/PageActions';
import ServiceContainer from '../config/ServiceContainer';
import { ServiceNames } from '../constant/ServiceNames';
import { ComponentState, setSelectedItems } from '../redux/actions/ComponentActions';
import StandardContainerComponent, { StandardSubscriptionComponentArgs } from '../helpers/StandardContainerComponent';
import LanguageUtil from '../util/LanguageUtil';
import { BulkActionDependencyError, BulkActionType } from 'redux/reducers/BulkActionReducer';
import CustomConfirmDialog from '../components/custom/CustomConfirmDialog';
import BulkActionUtil from '../util/BulkActionUtil';
import { dispatch } from '../redux/store';
import { openBulkActionDialog } from '../redux/actions/BulkActionActions';
import MessageUtil from '../util/MessageUtil';
import { ChannelUndeployDependent } from '../domain/stream/transaction/ChannelUndeployDependent';
import ChannelUndeployImpactDialog from '../components/transaction/ChannelUndeployImpactDialog';

class ChannelContainer extends StandardContainerComponent<Props, State, TransactionJsonModel, TransactionSchema> {
  private readonly transactionService = ServiceContainer.getInstance().get(ServiceNames.CHANNEL) as any;

  constructor(props: Props) {
    const args: StandardSubscriptionComponentArgs = {
      component: ComponentName.TRANSACTION,
      withSchema: true,
      itemCollection: CollectionNames.TRANSACTIONS,
      label: LanguageUtil.get({ id: `common.label.transaction`, defaultMessage: 'Transaction' }) + 's',
    };
    super(props, args);
    this.state = {
      ...this.state,
      undeployDialogVisible: false,
      selectedItemForUndeploy: null,
      dependentDialogVisible: false,
      undeployDependents: [],
    };
  }

  private getTransactionJsonModelsFromModalArray = (): string[] => {
    const {
      component: { selectedComponentIds },
    } = this.props;
    return selectedComponentIds.map((transaction: TransactionJsonModel) => transaction.id);
  };

  protected async deployAll(): Promise<BulkActionDependencyError[]> {
    return await this.transactionService.bulkAction(this.getTransactionJsonModelsFromModalArray(), 'Deploy');
  }

  protected async undeployAll(): Promise<BulkActionDependencyError[]> {
    return await this.transactionService.bulkAction(this.getTransactionJsonModelsFromModalArray(), 'Undeploy');
  }

  protected async removeAll(): Promise<BulkActionDependencyError[]> {
    return await this.transactionService.bulkAction(this.getTransactionJsonModelsFromModalArray(), 'Remove');
  }

  protected async prepareCount() {
    const count = await this.transactionService.getEventCount(this.getCountFilter().build());
    this.setState({ totalRecord: count });
  }

  protected getListView() {
    const { itemBuilderView, items, permissions } = this.state;
    return (
      <TransactionListViewerComponent
        views={itemBuilderView?.getListViews()}
        transactions={items}
        loading={this.state.loading}
        currentModal={itemBuilderView?.getLastCurrent()}
        onSortChange={this.onSortChange}
        openTransaction={this.openComponent}
        permissions={permissions}
        onUndeploy={this.showUndeployDialog}
      />
    );
  }

  public render() {
    const { undeployDialogVisible, dependentDialogVisible, undeployDependents } = this.state as State;
    return (
      <>
        <CustomConfirmDialog
          visible={undeployDialogVisible}
          onHide={this.hideUndeployDialog}
          icon={<img src={'/icons/error.svg'} alt='error' className='w-[172px] h-[172px] flex items-center justify-center' />}
          title={LanguageUtil.get({
            id: 'messages.confirm.undeploy1',
            defaultMessage: 'Are you sure you want to undeploy?',
          })}
          description={LanguageUtil.get({
            id: 'messages.confirm.undeployDescription',
            defaultMessage: 'If you undeploy, all data belonging to this component will be deleted.',
          })}
          cancelLabel={LanguageUtil.get({ id: 'common.label.cancel', defaultMessage: 'Cancel' })}
          confirmLabel={LanguageUtil.get({ id: 'common.label.undeploy', defaultMessage: 'Undeploy' })}
          onConfirm={this.confirmUndeploy}
        />
        <ChannelUndeployImpactDialog
          visible={dependentDialogVisible}
          dependents={undeployDependents}
          onCancel={this.cancelDependentUndeploy}
          onConfirm={this.confirmDependentUndeploy}
        />
        {super.render()}
      </>
    );
  }

  protected handleUndeployAll = async () => {
    const {
      component: { selectedComponentIds },
    } = this.props;
    const status = BulkActionUtil.analyzeSelectedItems(selectedComponentIds);
    const undeployableItems = BulkActionUtil.getUndeployableItems(selectedComponentIds);

    if (status.hasMixed && undeployableItems.length > 0) {
      const selectedStatuses = this.getSelectedStatuses(selectedComponentIds);
      dispatch(
        openBulkActionDialog({
          actionType: 'UNDEPLOY' as BulkActionType,
          componentName: this.args.component,
          selectedStatuses,
          hasMixedStatuses: true,
          onConfirm: async () => {
            await this.reviewBulkUndeploy();
          },
        }),
      );
    } else if (undeployableItems.length > 0) {
      this.setState({ undeployDialogVisible: true, selectedItemForUndeploy: null } as State);
    }
  };

  private showUndeployDialog = (item: TransactionJsonModel) => {
    this.setState({ undeployDialogVisible: true, selectedItemForUndeploy: item } as State);
  };

  private hideUndeployDialog = () => {
    this.setState({
      undeployDialogVisible: false,
      selectedItemForUndeploy: null,
      dependentDialogVisible: false,
      undeployDependents: [],
    } as State);
  };

  private cancelDependentUndeploy = () => {
    this.hideUndeployDialog();
  };

  private reviewBulkUndeploy = async () => {
    const ids = this.getTransactionJsonModelsFromModalArray().filter((id) => id != undefined && id.trim().length > 0);
    const dependents = await this.loadUndeployDependents(ids);
    if (dependents == undefined) {
      return;
    }
    if (dependents.length === 0) {
      await this.executeBulkAction(() => this.undeployAll(), 'UNDEPLOY');
      return;
    }
    this.setState({
      undeployDialogVisible: false,
      selectedItemForUndeploy: null,
      dependentDialogVisible: true,
      undeployDependents: dependents,
    } as State);
  };

  private confirmUndeploy = async () => {
    const ids = this.resolveUndeployIds();
    const dependents = await this.loadUndeployDependents(ids);
    if (dependents == undefined) {
      return;
    }
    if (dependents.length === 0) {
      await this.executeUndeploy();
      this.hideUndeployDialog();
      return;
    }
    this.setState({
      undeployDialogVisible: false,
      dependentDialogVisible: true,
      undeployDependents: dependents,
    } as State);
  };

  private confirmDependentUndeploy = async () => {
    await this.executeUndeploy();
    this.hideUndeployDialog();
  };

  private executeUndeploy = async () => {
    const { selectedItemForUndeploy } = this.state as State;

    if (selectedItemForUndeploy) {
      await this.transactionService.unDeployEvent(selectedItemForUndeploy);
      return;
    }
    await this.executeBulkAction(() => this.undeployAll(), 'UNDEPLOY');
  };

  private resolveUndeployIds(): string[] {
    const { selectedItemForUndeploy } = this.state as State;
    if (selectedItemForUndeploy != null) {
      return [selectedItemForUndeploy.name].filter((id) => id != undefined && id.trim().length > 0);
    }
    return this.getTransactionJsonModelsFromModalArray().filter((id) => id != undefined && id.trim().length > 0);
  }

  private loadUndeployDependents = async (ids: string[]): Promise<ChannelUndeployDependent[] | undefined> => {
    try {
      const dependents: ChannelUndeployDependent[] = [];
      for (const id of ids) {
        const found = await this.transactionService.listUndeployDependents(id);
        dependents.push(...found);
      }
      return this.uniqueDependents(dependents);
    } catch {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' }),
        detail: LanguageUtil.get({
          id: 'messages.error.undeployDependentsCheckFailed',
          defaultMessage: 'Bound components could not be listed. Undeploy was cancelled.',
        }),
      });
      return undefined;
    }
  };

  private uniqueDependents(dependents: ChannelUndeployDependent[]): ChannelUndeployDependent[] {
    const seen = new Set<string>();
    const unique: ChannelUndeployDependent[] = [];
    for (const dependent of dependents) {
      const key = `${dependent.componentType}:${dependent.name}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      unique.push(dependent);
    }
    return unique;
  }
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  itemView: state.page.itemView,
  currentCategory: state.page.currentCategory,
  pagePath: state.page.pagePath,
  categoryPages: state.category.pages,
  activePageIndex: state.page.activePageIndex,
  enrichedCategories: state.page.enrichedCategories,
  component: state.component as ComponentState,
  user: state.auth.user,
  attributes: state.auth.attributes,
});
const mapDispatchToProps = { addPagePath, changePagePath, clearPage, setSelectedItems, changePageFilter };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  undeployDialogVisible = false;
  selectedItemForUndeploy: TransactionJsonModel | null = null;
  dependentDialogVisible = false;
  undeployDependents: ChannelUndeployDependent[] = [];
}
export default withFormica(connector(ChannelContainer));
